import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Pagination from '../../components/common/Pagination'
import MonacoEditor from '../../components/common/MonacoEditor'
import HeadersConfig from '../../components/common/HeadersConfig'
import StepSelector from '../../components/common/StepSelector'
import JSONEditor from '../../components/common/JSONEditor'
import JSONOutput from '../../components/common/JSONOutput'
import Timeline from '../../components/common/Timeline'
import WizardStep from '../../components/common/WizardStep'
import api from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI } from '../../utils/apiHelpers'

const IntegrationMappingPage = ({ integration, onBack, token }) => {
    const [inputJson, setInputJson] = useState('');
    const [detectedFields, setDetectedFields] = useState([]);
    const [mappings, setMappings] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [showTestResult, setShowTestResult] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasUnsavedMappings, setHasUnsavedMappings] = useState(false);
    const [originalMappings, setOriginalMappings] = useState({});
    const [expandedFields, setExpandedFields] = useState({});
    const [showErrorsModal, setShowErrorsModal] = useState(false);
    const debounceTimer = useRef(null);

    // Detectar campos ID no JSON
    const detectIdFields = (jsonObj, path = '') => {
        const fields = [];
        // Detecta: id, userId, productId, user_id, id_user, idUser, etc.
        const idPattern = /^id|_id$|id[A-Z]|[a-z]id/i;

        try {
            if (typeof jsonObj === 'object' && jsonObj !== null && !Array.isArray(jsonObj)) {
                for (const [key, value] of Object.entries(jsonObj)) {
                    const currentPath = path ? `${path}.${key}` : key;
                    if (idPattern.test(key)) {
                        fields.push(currentPath);
                    }
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        fields.push(...detectIdFields(value, currentPath));
                    }
                }
            }
        } catch (error) {

        }
        return fields;
    };

    // Validar JSON
    const validateJson = () => {
        try {
            JSON.parse(inputJson);
            return true;
        } catch (error) {
            return false;
        }
    };

    // Detectar campos (SIMPLIFICADO)
    const handleDetectFields = () => {
        if (!validateJson()) {
            toast.error('JSON inválido');
            return;
        }

        try {
            const parsed = JSON.parse(inputJson);
            const fields = detectIdFields(parsed);
            setDetectedFields(fields);
            toast.info(`${fields.length} campo${fields.length !== 1 ? 's' : ''} encontrado${fields.length !== 1 ? 's' : ''}`);
        } catch (error) {
            toast.error('Erro ao detectar campos');
        }
    };

    // Salvar template de entrada (manual)
    const handleSaveTemplate = async () => {
        if (!validateJson()) {
            toast.error('JSON inválido');
            return;
        }

        setSavingTemplate(true);
        try {
            await api.put(`/integrations/${integration.id}/input-template`, {
                input_template: JSON.parse(inputJson)
            });
            setHasUnsavedChanges(false);
            toast.success('Template salvo com sucesso');
        } catch (error) {
            toast.error('Erro ao salvar template');
        } finally {
            setSavingTemplate(false);
        }
    };

    // Handle JSON input change (no auto-save, just track changes)
    const handleJsonChange = (newJson) => {
        setInputJson(newJson);
        setHasUnsavedChanges(true);
    };

    // Carregar template de entrada e mapeamentos existentes
    useEffect(() => {
        const loadData = async () => {
            try {
                // Carregar template de entrada
                try {
                    const templateResponse = await api.get(`/integrations/${integration.id}/input-template`);
                    if (templateResponse.data.input_template) {
                        const jsonStr = JSON.stringify(templateResponse.data.input_template, null, 2);
                        setInputJson(jsonStr);
                        setHasUnsavedChanges(false);

                        // Auto-detect ID fields when template is loaded
                        try {
                            const fields = detectIdFields(templateResponse.data.input_template);
                            if (fields.length > 0) {
                                setDetectedFields(fields);
                            }
                        } catch (error) {

                        }
                    }
                } catch (error) {
                    // Template might not exist yet
                }

                // Carregar mapeamentos
                try {
                    const response = await api.get(`/integrations/${integration.id}/field-mappings`, {
                        params: { page: 1, page_size: 100 }
                    });

                    const mappingsMap = {};
                    response.data.items.forEach(mapping => {
                        mappingsMap[mapping.field_path] = mapping;
                    });
                    setMappings(mappingsMap);
                    setOriginalMappings(mappingsMap);
                    setHasUnsavedMappings(false);
                } catch (error) {
                    // Mappings might not exist yet
                }
            } catch (error) {

            } finally {
            }
        };

        loadData();

        // Cleanup debounce timer on unmount
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [integration.id, token]);

    // Salvar mapeamentos (SIMPLIFICADO: delete tudo e recria)
    const handleSaveAllMappings = async () => {
        // Filtrar apenas mapeamentos válidos (com URL e parâmetro)
        const validMappings = Object.values(mappings).filter(m =>
            m.api_url && m.url_param_name
        );
        try {
            // 0. Atualizar input_template com o JSON atual

            try {
                await api.put(`/integrations/${integration.id}/input-template`, {
                    input_template: JSON.parse(inputJson)
                });
            } catch (e) {

                // Continua mesmo se falhar ao atualizar template
            }

            // 1. Carregar TODOS os mapeamentos salvos na integração

            let existingMappings = [];
            try {
                const loadResponse = await api.get(`/integrations/${integration.id}/field-mappings`, {
                    params: { page: 1, page_size: 100 }
                });
                existingMappings = loadResponse.data.items || [];
            } catch (e) {
                // Tabela pode não existir ainda - tenta continuar mesmo assim
                existingMappings = [];
            }

            // 2. Deletar TODOS os mapeamentos existentes (não apenas os em validMappings)

            const deletePromises = existingMappings.map(existingMapping =>
                api.delete(`/integrations/${integration.id}/field-mappings/${existingMapping.id}`)
                    .then(() => {

                    })
                    .catch(e => {

                    })
            );
            await Promise.all(deletePromises);

            // Pequeno delay após deletar para garantir sincronização
            await new Promise(resolve => setTimeout(resolve, 200));

            // 3. Se não há mapeamentos para salvar, só limpa
            if (validMappings.length === 0) {
                setMappings({});
                setOriginalMappings({});
                setHasUnsavedMappings(false);
                toast.success('Mapeamentos deletados');
                return true;
            }

            // 4. Criar NOVOS mapeamentos com dados atuais

            let allSuccessful = true;
            const createdMappings = {};

            // Salvar primeiro TODOS os mapeamentos
            for (const mapping of validMappings) {
                const headersToSend = Array.isArray(mapping.headers)
                    ? mapping.headers.filter(h => h && h.key && h.value)
                    : [];

                const payloadToSend = {
                    field_path: mapping.field_path,
                    api_url: mapping.api_url,
                    url_param_name: mapping.url_param_name,
                    headers: headersToSend,
                    transformed_field_name: mapping.transformed_field_name || null,
                    active: true
                };

                try {
                    const response = await api.post(`/integrations/${integration.id}/field-mappings`, payloadToSend);
                    createdMappings[mapping.field_path] = response.data;
                } catch (error) {
                    allSuccessful = false;
                    let errorMsg = `Erro ao criar mapeamento ${mapping.field_path}`;
                    if (error.response?.data?.detail) {
                        const detail = error.response.data.detail;
                        errorMsg = typeof detail === 'string' ? detail : JSON.stringify(detail);
                    }
                    toast.error(errorMsg);
                }
            }

            // 5. DEPOIS de salvar tudo, recarregar do servidor para verificar

            try {
                const reloadResponse = await api.get(`/integrations/${integration.id}/field-mappings`, {
                    params: { page: 1, page_size: 100 }
                });
                const mappingsMap = {};
                reloadResponse.data.items.forEach(mapping => {
                    mappingsMap[mapping.field_path] = mapping;
                });
                setMappings(mappingsMap);
                setOriginalMappings(mappingsMap);
            } catch (error) {
                // Continue even if reload fails
            }

            setHasUnsavedMappings(false);
            if (allSuccessful) {
                toast.success('Mapeamentos salvos com sucesso');
            }
            return allSuccessful;
        } catch (error) {

            toast.error('Erro ao salvar mapeamentos');
            return false;
        } finally {
        }
    };

    // Salvar e testar transformação
    const handleSaveAndTest = async () => {
        if (!validateJson()) {
            toast.error('JSON inválido');
            return;
        }

        // Limpar resultado anterior imediatamente
        setTestResult(null);
        setShowTestResult(false);

        // Salvar mapeamentos não salvos antes de testar
        let currentMappings = mappings;
        if (hasUnsavedMappings) {
            const savedSuccessfully = await handleSaveAllMappings();
            if (!savedSuccessfully) {
                toast.error('Falha ao salvar mapeamentos. Verifique os erros e tente novamente.');
                return;
            }

            // Recarregar mapeamentos do servidor para garantir sincronização

            try {
                const reloadResponse = await api.get(`/integrations/${integration.id}/field-mappings`, {
                    params: { page: 1, page_size: 100 }
                });
                const mappingsMap = {};
                reloadResponse.data.items.forEach(mapping => {
                    mappingsMap[mapping.field_path] = mapping;
                });
                currentMappings = mappingsMap;
            } catch (e) {
                currentMappings = mappings;
            }
        }

        // Usar mapeamentos salvos que existem no JSON
        const parsed = JSON.parse(inputJson);
        const activeMappings = Object.values(currentMappings).filter(m => {
            // Verificar se o campo existe no JSON
            const fieldExists = m.field_path.split('.').reduce((obj, key) => obj?.[key], parsed) !== undefined;
            return m.id && fieldExists;
        });

        if (activeMappings.length === 0) {
            const totalMappings = Object.values(mappings).filter(m => m.id).length;
            if (totalMappings > 0) {
                toast.warning('Nenhum dos mapeamentos existe no JSON. Verifique se os campos estão presentes.');
            } else {
                toast.error('Nenhum mapeamento configurado para testar');
            }
            return;
        }
        try {
            const response = await api.post(`/integrations/${integration.id}/field-mappings/test`, {
                input_json: JSON.parse(inputJson),
                mapping_ids: activeMappings.map(m => m.id)
            });

            setTestResult(response.data);
            setShowTestResult(true);
            if (response.data.success) {
                toast.success('Transformação realizada com sucesso');
            } else {
                const errorCount = response.data.errors.length;
                const errorPreview = response.data.errors.length > 0 ? response.data.errors[0].substring(0, 60) : '';
                toast.warning(`${errorCount} erro${errorCount !== 1 ? 's' : ''} encontrado${errorCount !== 1 ? 's' : ''} - Veja detalhes abaixo`);
            }
        } catch (error) {
            let errorMsg = 'Erro ao testar transformação';
            const errorData = error.response?.data;
            if (errorData) {
                if (errorData.detail) {
                    errorMsg = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
                } else if (Array.isArray(errorData)) {
                    errorMsg = errorData[0]?.msg || 'Erro ao testar transformação';
                } else if (errorData.msg) {
                    errorMsg = errorData.msg;
                }
            }
            toast.error(errorMsg);
        } finally {
        }
    };

    // Extrair parâmetro da URL automaticamente
    const extractParameterFromUrl = (url) => {
        // Encontra todos os parâmetros entre chaves: {algo}
        const matches = url.match(/\{([^}]+)\}/g);

        // Se encontrar exatamente um parâmetro, retorna ele
        if (matches && matches.length === 1) {
            return matches[0]; // Retorna com as chaves, ex: {id}
        }

        // Se encontrar múltiplos ou nenhum, retorna null
        return null;
    };

    // Atualizar campo de mapeamento
    const updateMappingField = (fieldPath, field, value) => {
        setMappings(prev => {
            const updated = {
                ...prev,
                [fieldPath]: {
                    field_path: fieldPath,
                    ...prev[fieldPath],
                    [field]: value
                }
            };

            // Se está atualizando a URL, tenta extrair o parâmetro automaticamente
            if (field === 'api_url' && value) {
                const extractedParam = extractParameterFromUrl(value);
                if (extractedParam && !updated[fieldPath].url_param_name) {
                    // Só preenche se o campo de parâmetro está vazio
                    updated[fieldPath].url_param_name = extractedParam;
                }
            }

            return updated;
        });
    };

    // Icons
    const backIcon = <span className="material-icons sm">arrow_back</span>;
    const transformIcon = <span className="material-icons sm">transform</span>;
    const saveIcon = <span className="material-icons sm">save</span>;
    const testIcon = <span className="material-icons sm">science</span>;
    const detectIcon = <span className="material-icons sm">search</span>;

    return (
        <div className="flex-1 overflow-hidden relative">
            <div className="p-8 h-full flex flex-col">
                {/* Loading Overlays */}
                {savingTemplate && (
                    <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Salvando...</span>
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between mb-8 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                            title="Voltar para integrações"
                        >
                            {backIcon}
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="material-icons text-purple-500 dark:text-purple-400" style={{fontSize: '32px'}}>transform</span>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{integration.name}</h1>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Mapeamento de Campos</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3-Column Layout */}
                <div className="grid grid-cols-10 gap-4 h-[calc(100vh-380px)] mb-8 items-start">
                    {/* Coluna Esquerda - JSON Input */}
                    <div className="col-span-4 h-[calc(100vh-380px)] overflow-hidden">
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 h-full flex flex-col">
                            <label className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex-shrink-0">JSON de Entrada</label>
                            <div className="flex-1 min-h-0 mb-4">
                                <MonacoEditor
                                    value={inputJson}
                                    onChange={handleJsonChange}
                                    language="json"
                                    height="100%"
                                    readOnly={false}
                                />
                            </div>
                            <div className="flex-shrink-0">
                                <Button
                                    onClick={handleDetectFields}
                                    variant="primary"
                                    disabled={!validateJson()}
                                    className="w-full"
                                >
                                    {detectIcon} Detectar Campos ID
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Coluna Central - Campos Detectados */}
                    <div className="col-span-3 h-[calc(100vh-380px)] overflow-hidden">
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 h-full flex flex-col">
                            <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Campos Detectados</h2>
                                <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400">
                                    {detectedFields.length}
                                </span>
                            </div>

                            {detectedFields.length === 0 ? (
                                <p className="text-gray-500 dark:text-gray-400 text-sm">Clique em "Detectar Campos ID" para começar</p>
                            ) : (
                                <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 p-3 flex flex-col">
                                    <div className="detected-fields-scroll flex-1 overflow-y-auto space-y-2 min-h-0">
                                        {detectedFields.map(fieldPath => {
                                        const mapping = mappings[fieldPath] || { field_path: fieldPath };
                                        const isSaved = !!mapping.id;
                                        const isValid = mapping.api_url && mapping.url_param_name;

                                        const isExpanded = expandedFields[fieldPath];

                                        return (
                                            <div
                                                key={fieldPath}
                                                className={`rounded-lg border-2 transition ${
                                                    isSaved
                                                        ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                                                        : !isValid
                                                        ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                                                }`}
                                            >
                                                {/* Acordeão Header */}
                                                <button
                                                    onClick={() => setExpandedFields(prev => ({ ...prev, [fieldPath]: !isExpanded }))}
                                                    className="w-full flex items-center justify-between p-3 hover:opacity-80 transition"
                                                >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <span className="material-icons text-gray-600 dark:text-gray-400" style={{fontSize: '18px', flexShrink: 0}}>
                                                            {isExpanded ? 'expand_less' : 'expand_more'}
                                                        </span>
                                                        <p className="text-xs font-mono text-gray-900 dark:text-gray-100 truncate">{fieldPath}</p>
                                                    </div>
                                                    {isSaved ? (
                                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium ml-2 flex-shrink-0">✓ Salvo</span>
                                                    ) : isValid ? (
                                                        <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium ml-2 flex-shrink-0">⚙ Não Salvo</span>
                                                    ) : null}
                                                </button>

                                                {/* Acordeão Content */}
                                                {isExpanded && (
                                                    <div className="px-3 pb-3 pt-3 space-y-2 border-t border-current border-opacity-10">
                                                        <input
                                                            placeholder="API URL"
                                                            value={mapping.api_url || ''}
                                                            onChange={(e) => {
                                                                updateMappingField(fieldPath, 'api_url', e.target.value);
                                                                setHasUnsavedMappings(true);
                                                            }}
                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500"
                                                            title="Ex: https://api.com/units/{id}"
                                                        />

                                                        <input
                                                            placeholder="Parâmetro"
                                                            value={mapping.url_param_name || ''}
                                                            onChange={(e) => {
                                                                updateMappingField(fieldPath, 'url_param_name', e.target.value);
                                                                setHasUnsavedMappings(true);
                                                            }}
                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500"
                                                            title="Ex: {id}"
                                                        />

                                                        <input
                                                            placeholder="Nome do Campo Transformado"
                                                            value={mapping.transformed_field_name || ''}
                                                            onChange={(e) => {
                                                                updateMappingField(fieldPath, 'transformed_field_name', e.target.value);
                                                                setHasUnsavedMappings(true);
                                                            }}
                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:border-blue-500"
                                                            title="Ex: unidade ou unit_data (como será chamado no JSON transformado)"
                                                        />

                                                        <div>
                                                            <HeadersConfig
                                                                headers={mapping.headers || []}
                                                                onChange={(headers) => {
                                                                    updateMappingField(fieldPath, 'headers', headers);
                                                                    setHasUnsavedMappings(true);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Coluna Direita - Ações e Resultado */}
                    <div className="col-span-3 h-[calc(100vh-380px)] overflow-hidden">
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 h-full flex flex-col">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Ações</h2>
                            <div className="flex-1 overflow-y-auto scroll-smooth flex flex-col min-h-0">

                                <Button
                                    onClick={handleSaveAndTest}
                                    variant="primary"
                                    disabled={detectedFields.length === 0}
                                    className="w-full flex-shrink-0"
                                >
                                    {testIcon} Salvar e Testar
                                </Button>

                                {showTestResult && testResult && (
                                    <div className="mt-6 flex-1 flex flex-col min-h-0 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">JSON Transformado</h3>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    const json = JSON.stringify(testResult.output_json, null, 2);
                                                    navigator.clipboard.writeText(json);
                                                    toast.success('JSON copiado');
                                                }}
                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition text-gray-600 dark:text-gray-400"
                                                title="Copiar JSON"
                                            >
                                                <span className="material-icons sm">content_copy</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const json = JSON.stringify(testResult.output_json, null, 2);
                                                    const blob = new Blob([json], { type: 'application/json' });
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `transformacao-${new Date().getTime()}.json`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    URL.revokeObjectURL(url);
                                                    toast.success('JSON baixado');
                                                }}
                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition text-gray-600 dark:text-gray-400"
                                                title="Baixar JSON"
                                            >
                                                <span className="material-icons sm">download</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-h-0 border border-gray-300 dark:border-gray-600 rounded">
                                        <MonacoEditor
                                            value={JSON.stringify(testResult.output_json, null, 2)}
                                            onChange={() => {}}
                                            language="json"
                                            height="100%"
                                            readOnly={true}
                                        />
                                    </div>

                                    {testResult.errors.length > 0 && (
                                        <button
                                            onClick={() => setShowErrorsModal(true)}
                                            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 border border-red-300 dark:border-red-700 rounded-lg transition text-red-700 dark:text-red-400"
                                        >
                                            <span className="material-icons" style={{fontSize: '18px'}}>error</span>
                                            <span className="text-sm font-medium">Ver Erros ({testResult.errors.length})</span>
                                        </button>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal de Erros - Fixada à direita */}
                {showErrorsModal && testResult && testResult.errors.length > 0 && (
                    <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg flex flex-col z-50">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="material-icons text-red-600 dark:text-red-400">error</span>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Erros na Transformação</h3>
                            </div>
                            <button
                                onClick={() => setShowErrorsModal(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                            >
                                <span className="material-icons">close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto scroll-smooth p-4 space-y-2">
                            {testResult.errors.map((error, idx) => (
                                <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                                    <p className="text-xs font-mono text-red-700 dark:text-red-300 break-words">
                                        <span className="font-bold text-red-600 dark:text-red-400">Erro {idx + 1}:</span> {error}
                                    </p>
                                </div>
                            ))}
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mt-4">
                                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">💡 Dicas para resolver:</p>
                                <ul className="list-disc list-inside space-y-1 text-xs text-blue-600 dark:text-blue-400">
                                    <li>Verifique as URLs das APIs configuradas</li>
                                    <li>Valide os parâmetros enviados na URL</li>
                                    <li>Confirme que as APIs estão acessíveis</li>
                                    <li>Verifique headers HTTP configurados</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IntegrationMappingPage
